import React, { useRef, useState } from "react";
import {
  DialogContent,
  Alert,
  FormControl,
  FormHelperText,
  Grid,
  Box,
  Typography,
  Link
} from "@mui/material";
import { PTForm, PTTextInput, PTTranslate, PTButton, PTTypography } from "../../../common/components/ui";
import { LoginValidationSchema } from "../others/LoginValidationSchema";
import ReCAPTCHA from "react-google-recaptcha";
import { LoginFormInitialValues } from "../others/LoginFormInitialValues";
import ForgotPasswordPage from "../../forget_password/page/ForgotPasswordPage";
import { useAuth } from "../../../common/provider";
import config from "../../../config";

const LoginForm = ({ onClose, login, error }) => {
  const { toggleInscriptionDialog } = useAuth();
  const [captchaChecked, setCaptchaChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [captchaError, setCaptchaError] = useState(false);
  const [captchaResponse, setCaptchaResponse] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const recaptchaRef = useRef(null);

  const resetCaptcha = () => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
    setCaptchaChecked(false);
    setCaptchaResponse(null);
  };

  const handleCaptcha = (value) => {
    if (value) {
      setCaptchaError(false);
      setCaptchaChecked(true);
      setCaptchaResponse(value);
    } else {
      setCaptchaChecked(false);
      setCaptchaError(true);
    }
  };

  const handleSubmit = (v) => {
    if (!captchaChecked) {
      setCaptchaError(true);
      return;
    }

    setErrorMessage(null);
    setIsLoading(true);

    login(v, captchaResponse)
      .then((success) => {
        setIsLoading(false);
        if (success) {
          onClose();
        } else {
          resetCaptcha();
        }
      })
      .catch((errorResponse) => {
        setIsLoading(false);
        if (errorResponse.response && errorResponse.response.data && errorResponse.response.data.message) {
          setErrorMessage(errorResponse.response.data.message);
        } else {
          setErrorMessage("An unexpected error occurred. Please try again.");
        }

      });
  };

  const inputStyles = {
    backgroundColor: "white ",
    color: "white ",
    "& .MuiOutlinedInput-root": {
        backgroundColor: "white ",
        "&.Mui-focused": {
            backgroundColor: "white "
        },
        "&:hover": {
            backgroundColor: "white "
        }
    },
    "& .MuiInputBase-input": {
        backgroundColor: "white ",
        padding: "8px 14px"
    }
};

  return (
    <>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          maxWidth: "100%",
          mx: "1%",
          textAlign: "center",
          fontWeight: 'bold',
          fontSize: '1.5rem',
          maxHeight: "auto",
          overflow: "hidden",
          position: "relative",
          paddingBottom: "60px",
          justifyContent: "space-between"
        }}
      >
        {isForgotPasswordMode ? (
          <ForgotPasswordPage onBackToLogin={() => setIsForgotPasswordMode(false)} />
        ) : (
          <>
            <PTTranslate tk="login.title" />
            {(errorMessage || error) && (
              <Alert severity="error" sx={{ mt: 2, mb: 3 }}>
                {errorMessage || error}
              </Alert>
            )}

            <Grid container justifyContent="center">
              <Grid item xs={12} md={10}>
                <PTForm
                  name="loginForm"
                  initialValues={LoginFormInitialValues}
                  onSubmit={handleSubmit}
                  submitButtonText={"login.submit"}
                  validationSchema={LoginValidationSchema}
                  retableOn={false}
                  fullWidthSubmitButton={true}
                  buttonStyle={{
                    width: "100%",
                    maxWidth: "100%",
                    backgroundColor: "#407BFF",
                    py: 1.1,
                    border: "none",
                    fontSize: "17px",
                    "&:hover": {
                      backgroundColor: "#407BFF",
                    },
                    "&:focus": {
                      backgroundColor: "#407BFF",
                    },
                  }}
                  isPending={isLoading}
                >
                  <PTTextInput
                    hiddenLabel={true}
                    name="username"
                    md={12}
                    lg={12}
                    placeHolder={"login.username"}
                    sx={inputStyles}
                  />
                  <PTTextInput
                    hiddenLabel={true}
                    name="password"
                    type="password"
                    placeHolder={"login.password"}
                    md={12}
                    lg={12}
                  />
                  <Grid item xs={12}>
                    <Link
                      component="button"
                      onClick={() => setIsForgotPasswordMode(true)}
                      underline="hover"
                      sx={{
                        fontSize: '0.875rem',
                        textAlign: 'right',
                        display: 'block',
                        background: "none",
                        border: "none",
                        cursor: "pointer"
                      }}
                    >
                      <PTTranslate tk="login.forgotPassword" />
                    </Link>
                  </Grid>
                  <Grid item xs={12}  >
                    <FormControl error={captchaError} sx={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%',
                    }}>
                      <Box
                        sx={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          maxWidth: '100%',
                          '& > div': {
                            width: '100%'
                          }
                        }}
                      >
                        
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey={config.recaptchaSiteKey}
                          onChange={handleCaptcha}
                          style={{
                            transform: 'scale(0.85)',
                          }}
                        />
                      </Box>
                      {!!captchaError && (
                        <FormHelperText error sx={{ textAlign: 'center' }}>
                          <PTTranslate tk="login.confirmNotRobot" />
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </PTForm>
              </Grid>
            </Grid>
            <Box
              sx={{
                mt: 2.1, 
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PTTypography
                variant="body2"
              >
                login.noAccount
              </PTTypography>
              <PTButton
                title={"login.registerHere"}
                variant="text"
                onClick={toggleInscriptionDialog}
              />
            </Box>
          </>
        )}
      </DialogContent>
    </>
  );
};

export default LoginForm;
